import { useEffect, useMemo, useState } from "react";
import { Button, Form, Offcanvas } from "react-bootstrap";
import { Country, City, ICountry, ICity } from "country-state-city";
import { OEMProfile } from "../../../types/Profiles/oemProfile.types";
import { updateOEMCompany } from "../../../services/oemProfile/oemProfile";
import {
  FaBuilding,
  FaIndustry,
  FaGlobe,
  FaEnvelope,
  FaPhone,
  FaLocationDot,
  FaCity,
  FaPenToSquare,
  FaCircleInfo,
} from "react-icons/fa6";
import { toast } from "react-toastify";
import { useTranslate } from "../../../hooks/LanguageProvider";
import {
  AIDA_BADGE_OPTIONS,
  AIDA_CATEGORY_OPTIONS,
  AIDA_CLASSIFICATION_NOTICE,
  AIDA_COMPANY_TYPE_OPTIONS,
  AIDA_STATUS_OPTIONS,
  getAidaOptionLabel,
} from "../../../constants/aidaClassification";


interface Props {
  profile: OEMProfile;
  userId?: number | string | null;
  onProfileUpdated?: () => void;
}

type CompanyFormState = {
  companyName: string;
  industry: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  country: string;
  countryCode: string;
  city: string;
  headquarters: string;
  description: string;
  companyType: string;
  aidaCategory: string;
  aidaStatus: string;
  aidaBadge: string;
  aidaScore: number;
  aidaOutOfScope: number;
  aidaOutOfScopeReason: string;
};

const normalizeWebsiteUrl = (value?: string) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const getWebsiteDisplayValue = (value?: string) => {
  const normalizedUrl = normalizeWebsiteUrl(value);
  if (!normalizedUrl) return "-";

  try {
    const url = new URL(normalizedUrl);
    return url.hostname.replace(/^www\./i, "") + url.pathname.replace(/\/$/, "");
  } catch {
    return (value || "").trim();
  }
};

export default function CompanyTab({
  profile,
  userId,
  onProfileUpdated,
}: Props) {
  const [showDrawer, setShowDrawer] = useState(false);
  const { translate } = useTranslate();
  const [formData, setFormData] = useState<CompanyFormState>({
    companyName: "",
    industry: "",
    website: "",
    contactEmail: "",
    contactPhone: "",
    country: "",
    countryCode: "",
    city: "",
    headquarters: "",
    description: "",
    companyType: "oem_constructeur",
    aidaCategory: "oem_constructeur",
    aidaStatus: "inscrit",
    aidaBadge: "aida_verified",
    aidaScore: 0,
    aidaOutOfScope: 0,
    aidaOutOfScopeReason: "",
  });

  const allCountries = useMemo<ICountry[]>(
    () => Country.getAllCountries(),
    []
  );

  const filteredCities = useMemo<ICity[]>(() => {
    if (!formData.countryCode) return [];
    return City.getCitiesOfCountry(formData.countryCode) || [];
  }, [formData.countryCode]);

  useEffect(() => {
    const matchedCountry =
      allCountries.find(
        (c: ICountry) =>
          c.name.toLowerCase() === (profile.identity.country || "").toLowerCase()
      ) || null;

    setFormData({
      companyName: profile.identity.companyName ?? "",
      industry: profile.identity.industry ?? "",
      website: profile.identity.website ?? "",
      contactEmail: profile.identity.contactEmail ?? "",
      contactPhone: profile.identity.contactPhone ?? "",
      country: matchedCountry?.name ?? profile.identity.country ?? "",
      countryCode: matchedCountry?.isoCode ?? "",
      city: profile.identity.city ?? "",
      headquarters: profile.identity.headquarters ?? "",
      description: profile.identity.description ?? "",
      companyType: profile.identity.companyType ?? "oem_constructeur",
      aidaCategory: profile.identity.aidaCategory ?? "oem_constructeur",
      aidaStatus: profile.identity.aidaStatus ?? "inscrit",
      aidaBadge: profile.identity.aidaBadge ?? "aida_verified",
      aidaScore: Number(profile.identity.aidaScore || 0),
      aidaOutOfScope: Number(profile.identity.aidaOutOfScope || 0),
      aidaOutOfScopeReason: profile.identity.aidaOutOfScopeReason ?? "",
    });
  }, [profile, allCountries]);

  const handleOpenDrawer = () => {
    const matchedCountry =
      allCountries.find(
        (c: ICountry) =>
          c.name.toLowerCase() === (profile.identity.country || "").toLowerCase()
      ) || null;

    setFormData({
      companyName: profile.identity.companyName ?? "",
      industry: profile.identity.industry ?? "",
      website: profile.identity.website ?? "",
      contactEmail: profile.identity.contactEmail ?? "",
      contactPhone: profile.identity.contactPhone ?? "",
      country: matchedCountry?.name ?? profile.identity.country ?? "",
      countryCode: matchedCountry?.isoCode ?? "",
      city: profile.identity.city ?? "",
      headquarters: profile.identity.headquarters ?? "",
      description: profile.identity.description ?? "",
      companyType: profile.identity.companyType ?? "oem_constructeur",
      aidaCategory: profile.identity.aidaCategory ?? "oem_constructeur",
      aidaStatus: profile.identity.aidaStatus ?? "inscrit",
      aidaBadge: profile.identity.aidaBadge ?? "aida_verified",
      aidaScore: Number(profile.identity.aidaScore || 0),
      aidaOutOfScope: Number(profile.identity.aidaOutOfScope || 0),
      aidaOutOfScopeReason: profile.identity.aidaOutOfScopeReason ?? "",
    });

    setShowDrawer(true);
  };

  const handleCloseDrawer = () => setShowDrawer(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    const matchedCountry =
      allCountries.find(
        (c: ICountry) => c.name.toLowerCase() === value.toLowerCase()
      ) || null;

    setFormData((prev) => ({
      ...prev,
      country: value,
      countryCode: matchedCountry?.isoCode ?? "",
      city: matchedCountry ? "" : prev.city,
    }));
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      city: value,
    }));
  };

const handleSave = async () => {
  try {
    await updateOEMCompany(formData, userId);
    toast.success("Company updated successfully");
    setShowDrawer(false);
    onProfileUpdated?.();
  } catch (error: any) {
    console.error(error);
    toast.error(error?.response?.data?.error || "Failed to update company");
  }
};

  const infoCards = useMemo(
    () => [
      {
        key: "companyName",
        label: translate("company_name"),
        value: formData.companyName || "-",
        icon: <FaBuilding />,
        iconBg: "#FFF4D6",
        iconColor: "#D39E00",
      },
      {
        key: "industry",
        label: translate("industry"),
        value: formData.industry || "-",
        icon: <FaIndustry />,
        iconBg: "#EAF2FF",
        iconColor: "#0D6EFD",
      },
      {
        key: "website",
        label: translate("website"),
        value: getWebsiteDisplayValue(formData.website),
        href: normalizeWebsiteUrl(formData.website),
        icon: <FaGlobe />,
        iconBg: "#EAF8EE",
        iconColor: "#198754",
      },
      {
        key: "contactEmail",
        label: translate("contact_email"),
        value: formData.contactEmail || "-",
        icon: <FaEnvelope />,
        iconBg: "#F3EFFF",
        iconColor: "#7C3AED",
      },
      {
        key: "contactPhone",
        label: translate("contact_phone"),
        value: formData.contactPhone || "-",
        icon: <FaPhone />,
        iconBg: "#FFF1F0",
        iconColor: "#DC3545",
      },
      {
        key: "country",
        label: translate("country"),
        value: formData.country || "-",
        icon: <FaLocationDot />,
        iconBg: "#EEF2F7",
        iconColor: "#495057",
      },
      {
        key: "city",
        label: translate("city"),
        value: formData.city || "-",
        icon: <FaCity />,
        iconBg: "#F5F3FF",
        iconColor: "#6F42C1",
      },
      {
        key: "headquarters",
        label: translate("headquarters"),
        value: formData.headquarters || "-",
        icon: <FaBuilding />,
        iconBg: "#FFF8E1",
        iconColor: "#D39E00",
      },
      {
        key: "companyType",
        label: "Type de societe",
        value: getAidaOptionLabel(AIDA_COMPANY_TYPE_OPTIONS, formData.companyType),
        icon: <FaIndustry />,
        iconBg: "#FFF4D6",
        iconColor: "#D39E00",
      },
      {
        key: "aidaCategory",
        label: "Categorie AIDA",
        value: getAidaOptionLabel(AIDA_CATEGORY_OPTIONS, formData.aidaCategory),
        icon: <FaCircleInfo />,
        iconBg: "#EAF8EE",
        iconColor: "#198754",
      },
      {
        key: "aidaBadge",
        label: "Badge AIDA",
        value: getAidaOptionLabel(AIDA_BADGE_OPTIONS, formData.aidaBadge),
        icon: <FaCircleInfo />,
        iconBg: "#EAF2FF",
        iconColor: "#0D6EFD",
      },
      {
        key: "aidaScore",
        label: "Score AIDA",
        value: `${formData.aidaScore || 0}/100`,
        icon: <FaCircleInfo />,
        iconBg: "#EEF2F7",
        iconColor: "#495057",
      },
    ],
    [formData, translate]
  );

  return (
    <>
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h4 className="fw-semibold mb-1">{translate("company_information")}</h4>
              <p className="text-muted mb-0">
                {translate("oem_identity_location_contact_description")}
              </p>
            </div>

            <Button
              variant="warning"
              className="rounded-3 px-4 fw-semibold d-inline-flex align-items-center gap-2"
              onClick={handleOpenDrawer}
            >
              <FaPenToSquare size={15} />
              {translate("edit_company_information")}
            </Button>
          </div>

          <div className="row g-4">
            {infoCards.map((item) => (
              <div className="col-xl-3 col-md-6" key={item.key}>
                <div
                  className="border rounded-4 p-4 h-100"
                  style={{
                    background: "#fff",
                    minHeight: 150,
                    transition: "all 0.25s ease",
                  }}
                >
                  <div className="d-flex align-items-start gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-4"
                      style={{
                        width: 52,
                        height: 52,
                        backgroundColor: item.iconBg,
                        color: item.iconColor,
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </div>

                    <div className="w-100">
                      <small className="text-muted d-block mb-1">
                        {item.label}
                      </small>
                      {item.key === "website" && item.href ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noreferrer"
                          className="fw-semibold text-decoration-none"
                          title={item.href}
                          style={{
                            color: "#0f172a",
                            lineHeight: 1.4,
                            overflowWrap: "break-word",
                          }}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <div
                          className="fw-semibold"
                          style={{
                            wordBreak: "break-word",
                            lineHeight: 1.4,
                          }}
                        >
                          {item.value}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="border rounded-4 p-4 mt-4"
            style={{ background: "#fffdf5" }}
          >
            <div className="d-flex align-items-start gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: 42,
                  height: 42,
                  background: "#FFF4D6",
                  color: "#D39E00",
                  flexShrink: 0,
                }}
              >
                <FaCircleInfo />
              </div>

              <div>
                <h6 className="fw-semibold mb-1">{translate("company_description")}</h6>
                <p className="text-muted mb-0">
                  {formData.description || "No description available."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Offcanvas
        show={showDrawer}
        onHide={handleCloseDrawer}
        placement="end"
        backdrop
        style={{ width: window.innerWidth > 992 ? "50vw" : "100%" }}
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-semibold">
            {translate("edit_company_information")}
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <div className="mb-4">
            <p className="text-muted mb-0">
              Update the OEM company identity and contact information.
            </p>
          </div>

          <Form>
            <div className="row g-3">

              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">{translate("company_name")}</Form.Label>
                <Form.Control
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="rounded-3"
                />
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">{translate("industry")}</Form.Label>
                <Form.Control
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  className="rounded-3"
                />
              </Form.Group>

              <div className="col-12">
                <div className="border rounded-4 p-3" style={{ background: "#fffdf5" }}>
                  <h6 className="fw-semibold mb-1">Classification AIDA</h6>
                  <p className="text-muted small mb-3">{AIDA_CLASSIFICATION_NOTICE}</p>

                  <div className="row g-3">
                    <Form.Group className="col-md-6">
                      <Form.Label className="fw-semibold">Type de societe</Form.Label>
                      <Form.Select
                        value={formData.companyType}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            companyType: e.target.value,
                          }))
                        }
                        className="rounded-3"
                      >
                        {AIDA_COMPANY_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="col-md-6">
                      <Form.Label className="fw-semibold">Categorie AIDA</Form.Label>
                      <Form.Select
                        value={formData.aidaCategory}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            aidaCategory: e.target.value,
                          }))
                        }
                        className="rounded-3"
                      >
                        {AIDA_CATEGORY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="col-md-4">
                      <Form.Label className="fw-semibold">Statut AIDA</Form.Label>
                      <Form.Select
                        value={formData.aidaStatus}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            aidaStatus: e.target.value,
                          }))
                        }
                        className="rounded-3"
                      >
                        {AIDA_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="col-md-4">
                      <Form.Label className="fw-semibold">Badge AIDA</Form.Label>
                      <Form.Select
                        value={formData.aidaBadge}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            aidaBadge: e.target.value,
                          }))
                        }
                        className="rounded-3"
                      >
                        {AIDA_BADGE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="col-md-4">
                      <Form.Label className="fw-semibold">Score AIDA</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={100}
                        value={formData.aidaScore}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            aidaScore: Math.max(
                              0,
                              Math.min(100, Number(e.target.value) || 0),
                            ),
                          }))
                        }
                        className="rounded-3"
                      />
                    </Form.Group>

                    <div className="col-12">
                      <Form.Check
                        type="checkbox"
                        id="oem-aida-out-of-scope"
                        label="Hors perimetre AIDA - Importateur / Distributeur"
                        checked={Number(formData.aidaOutOfScope) === 1}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            aidaOutOfScope: e.target.checked ? 1 : 0,
                            aidaCategory: e.target.checked
                              ? "hors_perimetre_importateur"
                              : prev.aidaCategory,
                            aidaStatus: e.target.checked
                              ? "hors_perimetre"
                              : prev.aidaStatus,
                            aidaOutOfScopeReason: e.target.checked
                              ? "Importateur / Distributeur"
                              : "",
                          }))
                        }
                      />
                    </div>

                    {Number(formData.aidaOutOfScope) === 1 && (
                      <Form.Group className="col-12">
                        <Form.Label className="fw-semibold">
                          Motif hors perimetre
                        </Form.Label>
                        <Form.Control
                          value={formData.aidaOutOfScopeReason}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              aidaOutOfScopeReason: e.target.value,
                            }))
                          }
                          className="rounded-3"
                        />
                      </Form.Group>
                    )}
                  </div>
                </div>
              </div>

              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">{translate("website")}</Form.Label>
                <Form.Control
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="rounded-3"
                />
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">{translate("contact_email")}</Form.Label>
                <Form.Control
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  className="rounded-3"
                />
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">{translate("phone")}</Form.Label>
                <Form.Control
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  className="rounded-3"
                />
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">{translate("country")}</Form.Label>
                <Form.Control
                  list="country-list"
                  value={formData.country}
                  onChange={handleCountryChange}
                  placeholder="Search or select a country"
                  className="rounded-3"
                />
                <datalist id="country-list">
                  {allCountries.map((country: ICountry) => (
                    <option key={country.isoCode} value={country.name} />
                  ))}
                </datalist>
                <Form.Text className="text-muted">
                  Start typing to search in the country list.
                </Form.Text>
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">{translate("city")}</Form.Label>
                <Form.Control
                  list="city-list"
                  value={formData.city}
                  onChange={handleCityChange}
                  placeholder={
                    formData.countryCode
                      ? "Search or select a city"
                      : "Select a country first"
                  }
                  className="rounded-3"
                  disabled={!formData.countryCode}
                />
                <datalist id="city-list">
                  {filteredCities.map((city: ICity, index: number) => (
                    <option key={`${city.name}-${index}`} value={city.name} />
                  ))}
                </datalist>
                <Form.Text className="text-muted">
                  The city list is filtered by the selected country.
                </Form.Text>
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">{translate("headquarters")}</Form.Label>
                <Form.Control
                  name="headquarters"
                  value={formData.headquarters}
                  onChange={handleInputChange}
                  className="rounded-3"
                  placeholder="Example: Turin, Italy"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">{translate("description")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="rounded-3"
                />
              </Form.Group>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="light"
                  onClick={handleCloseDrawer}
                  className="rounded-3 px-4"
                >
                     {translate("cancel")}
                </Button>

                <Button
                  variant="warning"
                  onClick={handleSave}
                  className="rounded-3 px-4 fw-semibold"
                >
                  {translate("save")}
                </Button>
              </div>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
